import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  ChefHat, LayoutDashboard, UtensilsCrossed, Boxes, BarChart3, Users,
  CalendarClock, Sparkles, Store, CreditCard, LogOut, Menu as MenuIcon, X,
  ShoppingBag, Gift, UserCog, Globe, ClipboardList, Wallet, Palette, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LOGO } from "@/lib/brand";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/dashboard/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/dashboard/order-form", icon: ClipboardList, label: "Order Form", ownerOnly: true },
  { to: "/dashboard/menu", icon: UtensilsCrossed, label: "Menu" },
  { to: "/dashboard/website-builder", icon: Palette, label: "Website Builder", ownerOnly: true },
  { to: "/dashboard/website", icon: Globe, label: "Website Preview" },
  { to: "/dashboard/inventory", icon: Boxes, label: "Inventory" },
  { to: "/dashboard/daily-inventory", icon: ClipboardCheck, label: "Daily Inventory" },
  { to: "/dashboard/sales", icon: BarChart3, label: "Sales" },
  { to: "/dashboard/employees", icon: CalendarClock, label: "Staff & Clock" },
  { to: "/dashboard/salaries", icon: Wallet, label: "Salaries", payroll: true },
  { to: "/dashboard/customers", icon: Users, label: "Customers" },
  { to: "/dashboard/gift-cards", icon: Gift, label: "Gift Cards" },
  { to: "/dashboard/forecasting", icon: Sparkles, label: "AI Forecasting" },
  { to: "/dashboard/profile", icon: Store, label: "Profile & QR" },
  { to: "/dashboard/staff-accounts", icon: UserCog, label: "Staff Accounts", ownerOnly: true },
  { to: "/dashboard/billing", icon: CreditCard, label: "Billing", ownerOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const sub = user?.subscription;
  const onTrial = sub?.status === "trial";
  const visibleNav = NAV.filter((n) => {
    if (n.payroll) return user?.role !== "staff" || user?.can_view_payroll;
    return !n.ownerOnly || user?.role !== "staff";
  });

  if (user?.role === "admin") return <Navigate to="/admin" replace />;

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-border">
        <img src={LOGO} alt="manage.kitchen" className="h-10 w-auto object-contain" />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto app-scroll">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4.5 w-4.5" strokeWidth={1.6} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <Button data-testid="logout-btn" onClick={doLogout} variant="ghost"
          className="w-full justify-start text-foreground/70 hover:bg-muted">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card flex-col fixed inset-y-0">
        {Sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card border-r border-border">{Sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8">
          <button data-testid="mobile-sidebar-toggle" className="lg:hidden" onClick={() => setOpen(true)}>
            <MenuIcon />
          </button>
          <div className="hidden lg:block" />
          {onTrial ? (
            <div data-testid="trial-banner" className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                Free trial · <span className="font-semibold text-foreground">{sub?.trial_days_left ?? 14} days left</span>
              </span>
              <Button size="sm" onClick={() => navigate("/dashboard/billing")}
                className="bg-primary hover:bg-[#B35A42] text-primary-foreground h-8">Upgrade</Button>
            </div>
          ) : (
            <span data-testid="plan-badge" className="text-sm">
              <span className="text-muted-foreground">Plan: </span>
              <span className="font-semibold capitalize">{sub?.plan}</span>
            </span>
          )}
        </header>
        <main className="p-4 sm:p-8 max-w-7xl mx-auto app-scroll">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
